import { useEffect, useRef, useState } from 'react';
import './CustomCamera.scss';
import { useOrientation } from './useOrientation';
import { addExifBytes, goFullscreen, isTorchSupported } from './camera.utils';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import flashOffSvg from './../../assets/svg/flashOff.svg'
import flashOnSvg from './../../assets/svg/flashOn.svg'

interface CustomCameraProps {
  customContainerClass?: string;
  onCloseHandler: any;
  setPhotoHandler: any;
  rightItem?: any;
  leftItem?: any;
  mainLabelItem?: any;
  overlaySuggestItem?: any;
  isiOS?: boolean
  exifBytes?: any
}

const INTERVAL_BETWEEN_PHOTOS_MS = 1400

/* istanbul ignore next */
const CustomCamera = (props: CustomCameraProps) => {
  const { onCloseHandler, setPhotoHandler, customContainerClass,
    rightItem, leftItem, mainLabelItem, overlaySuggestItem, isiOS, exifBytes } = props;

  const [torchSupported, setTorchSupported] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [cameraStarted, setCameraStarted] = useState(false)
  const [takePhotoDisabled, setTakePhotoDisabled] = useState(false)
  const iosClassName = isiOS ? 'ios-device' : ''

  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);

  const orientation = useOrientation();

  useEffect(() => {
    async function checkTorch() {
      const supported = await isTorchSupported();
      setTorchSupported(supported);
    }

    checkTorch();
  }, []);

  const closeCameraHandler = () => {
    onCloseHandler && onCloseHandler();
  };

  /* istanbul ignore next */
  const enterFullscreen = async (el: HTMLElement) => {
    try {
      if (document.fullscreenElement) return;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else {
        // iOS Safari (15.4+): no standard fullscreen for arbitrary elements,
        // but using 100dvh + viewport-fit=cover + safe-area insets works.
        // iOS Safari fallback: add fullscreen class
        //el.style.height = '100dvh';
        // el.classList.add('fullscreen-camera');
        el.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 9999;
      `;
      }
    } catch (e) {
      console.warn('Fullscreen not available:', e);
    }
  }

  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment'  // Remove 'exact'
          }
        });

        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        videoTrackRef.current = track;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          enterFullscreen(containerRef.current!)
          // compute exif data
          setCameraStarted(true)
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        alert('Camera access denied or unavailable.');
      }
    };

    startCamera();
  }, []);

  const toggleFlash = async () => {
    const track = videoTrackRef.current;
    if (!track) return;

    try {
      await track.applyConstraints({
        advanced: [{ torch: !flashOn }]
      } as any);
      setFlashOn(prev => !prev);
    } catch (err) {
      console.warn('Torch toggle failed:', err);
    }
  };

  const temporarelyDisableTakePhoto = () => {
    setTakePhotoDisabled(true)
    setTimeout(() => {
      setTakePhotoDisabled(false)
    }, INTERVAL_BETWEEN_PHOTOS_MS)
  }

  async function takePhoto(): Promise<void> {
    if (!cameraStarted) return

    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    temporarelyDisableTakePhoto()
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.drawImage(video, 0, 0)
      let dataUrl = canvas.toDataURL('image/jpeg', 0.92)

      if (exifBytes) {
        dataUrl = addExifBytes(exifBytes, dataUrl)
      }

      setPhotoHandler(dataUrl)
    }
  }

  useEffect(() => {
    goFullscreen();
    return () => {
      // exitFullscreen();
      // Stop all camera tracks on unmount
      streamRef.current?.getTracks?.().forEach(track => track.stop());
    };
  }, []);

  const flashOnClass = flashOn ? 'flash-on' : 'flash-off'
  const cameraNotRedyClass = (cameraStarted && !takePhotoDisabled) ? '' : 'not-ready'

  return (
    <div
      ref={containerRef}
      className={`camera-capture-container ${orientation} ${customContainerClass} ${iosClassName}`}>
      {
        torchSupported && (<button className={`flash-container cmd-btn ${flashOnClass}`}
          onClick={toggleFlash}>
          {flashOn ?
            <img src={flashOnSvg} alt="flash on"
              className='flash-icon flash-on' /> :
            <img src={flashOffSvg} alt="flash off"
              className='flash-icon flash-off' />
          }
        </button>
        )}
      <button
        className={`close-camera cmd-btn`}
        onClick={closeCameraHandler}>
        <FontAwesomeIcon
          icon={faXmark} />
      </button>
      <button
        onClick={takePhoto}
        className={`take-photo-btn ${cameraNotRedyClass}`}>
        <span className='outer-circle'></span>
      </button>
      {rightItem &&
        <div className='right-item-container'>
          {rightItem}
        </div>
      }
      {leftItem &&
        <div className='left-item-container'>
          {leftItem}
        </div>
      }
      {mainLabelItem &&
        <div className='main-label-item-container'>
          {mainLabelItem}
        </div>
      }
      {overlaySuggestItem &&
        <div className='overlay-suggested-item-container'>
          {overlaySuggestItem}
        </div>
      }

      <div className="video-container">
        <video ref={videoRef} autoPlay playsInline />
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default CustomCamera;
